import * as React from "react";

interface TabsProps {
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
  children: React.ReactNode;
}

export const Tabs: React.FC<TabsProps> = ({ value, onValueChange, className, children }) => {
  const [active, setActive] = React.useState(value);

  React.useEffect(() => {
    setActive(value);
  }, [value]);

  const handleTabChange = (val: string) => {
    setActive(val);
    onValueChange(val);
  };

  // Provide context for triggers and content
  return (
    <TabsContext.Provider value={{ active, onTabChange: handleTabChange }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
};

interface TabsContextType {
  active: string;
  onTabChange: (value: string) => void;
}

const TabsContext = React.createContext<TabsContextType | undefined>(undefined);

interface TabsListProps {
  className?: string;
  children: React.ReactNode;
}
export const TabsList: React.FC<TabsListProps> = ({ className, children }) => (
  <div className={className} role="tablist">{children}</div>
);

interface TabsTriggerProps {
  value: string;
  children: React.ReactNode;
}
export const TabsTrigger: React.FC<TabsTriggerProps> = ({ value, children }) => {
  const ctx = React.useContext(TabsContext);
  if (!ctx) throw new Error("TabsTrigger must be used within Tabs");
  const isActive = ctx.active === value;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      className={`px-4 py-2 border-b-2 ${isActive ? "border-primary font-bold" : "border-transparent"}`}
      onClick={() => ctx.onTabChange(value)}
    >
      {children}
    </button>
  );
};

interface TabsContentProps {
  value: string;
  children: React.ReactNode;
}
export const TabsContent: React.FC<TabsContentProps> = ({ value, children }) => {
  const ctx = React.useContext(TabsContext);
  if (!ctx) throw new Error("TabsContent must be used within Tabs");
  if (ctx.active !== value) return null;
  return <div className="mt-4">{children}</div>;
};
